import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Colors } from "../../assets";
import { OrderCardInterface, OrderStatus } from "../../interfaces_and_types";
import OrderCardItem from "../OrderCardItem/orderCardItem";
import "./styles.css";
import { FaCreditCard, FaMoneyBill } from "react-icons/fa";
import { AiOutlinePrinter } from "react-icons/ai";
import { useRootStore } from "../../stores";
import { useLocation } from "react-router-dom";
import Item from "../../interfaces_and_types/orderItemType";
import AppPopup from "../AppPopup/AppPopup";
import { CircularProgress } from "@mui/material";
import OrderTicketV1 from "../OrderTicket/orderTicketV1";
import { printOrderTicket } from "../../services/printTicket";

// Accepts "HH:MM:SS", "H:MM:SS" (str(timedelta) from the backend), with an
// optional ".uuuuuu" fraction. Hours are kept, not dropped.
const parseDurationMs = (time: string | null | undefined): number | null => {
  if (time === null || time === undefined) return null;
  const match = /^(\d+):([0-5]?\d):([0-5]?\d)/.exec(time.trim());
  if (match === null) return null;
  return (
    (parseInt(match[1], 10) * 3600 +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10)) *
    1000
  );
};

const OrderCard = (props: OrderCardInterface) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { place, token } = useRootStore().rootStore.placeStore;
  const { dragToReady } = useRootStore().rootStore.orderStore;
  const [popupOpen, setPopUpOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(() =>
    parseDurationMs(props.order.estimatedTime),
  );
  const [startButtonLoading, setStartButtonLoading] = useState(false);
  const [cancelButtonLoading, setCancelButtonLoading] = useState(false);
  const orderCardRef = useRef<HTMLDivElement | null>(null);
  // The countdown is anchored to an absolute wall-clock deadline so that
  // sleep, tab throttling, and render lag all self-correct on the next tick.
  const deadlineRef = useRef<number | null>(null);
  // Auto-ready only if this card actually observed time remaining — a card
  // that mounts already at 00:00 (e.g. estimatedTime never set) must not
  // silently move the order to Ready.
  const hadTimeRef = useRef(false);
  const autoReadyFiredRef = useRef(false);

  // Re-anchor whenever the server (or the start-order popup) supplies a fresh
  // remaining time. The backend returns time *remaining* on every fetch, so a
  // refetch after wake-from-sleep corrects the deadline automatically.
  useEffect(() => {
    const durationMs = parseDurationMs(props.order.estimatedTime);
    deadlineRef.current = durationMs !== null ? Date.now() + durationMs : null;
    setRemainingMs(durationMs);
    if (durationMs !== null && durationMs > 0) {
      hadTimeRef.current = true;
      autoReadyFiredRef.current = false;
    }
  }, [props.order.estimatedTime]);

  useEffect(() => {
    if (!props.order.isPreparing) {
      hadTimeRef.current = false;
      autoReadyFiredRef.current = false;
      return;
    }
    const tick = () => {
      if (deadlineRef.current === null) return;
      const left = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(left);
      if (left > 0) {
        hadTimeRef.current = true;
      } else if (
        hadTimeRef.current &&
        !autoReadyFiredRef.current &&
        token !== null
      ) {
        autoReadyFiredRef.current = true;
        dragToReady(props.order.id, token);
      }
    };
    tick();
    const timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.order.isPreparing, props.order.id, token]);

  const totalSecsLeft =
    remainingMs !== null ? Math.ceil(remainingMs / 1000) : 0;
  const mins = Math.floor(totalSecsLeft / 60);
  const secs = totalSecsLeft % 60;

  const convertDate = () => {
    if (place === null) return;
    const date = new Date(props.order.orderDate);
    const pad = (num: number) => {
      if (num < 10) return "0" + num;
      return num;
    };
    if (pathname === `/orders-history`)
      return (
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes()) +
        "  " +
        pad(date.getDate()) +
        "/" +
        pad(date.getMonth() + 1) +
        "/" +
        date.getFullYear()
      );
    return pad(date.getHours()) + ":" + pad(date.getMinutes());
  };
  const [openBody, setOpenBody] = useState(false);
  const { updateOrder, updateOrderItem, reportNoShow, updateOrderPickUp } =
    useRootStore().rootStore.orderStore;
  const [orderFade, setOrderFade] = useState(false);
  const [moveLeft, setMoveLeft] = useState(false);
  const [cashActionLoading, setCashActionLoading] = useState<
    null | "no_show" | "refuse" | "picked_up"
  >(null);
  const [noShowReported, setNoShowReported] = useState(false);
  const [cashConfirmReason, setCashConfirmReason] = useState<
    null | "customer_no_show" | "refuse_to_pay"
  >(null);
  const [printLoading, setPrintLoading] = useState(false);

  useEffect(() => {
    //console.log(toJS(props.order));
  }, [openBody]);

  const updateItem = async (item: Item) => {
    if (token === null) return;
    if (props.order.isPreparing) {
      const res = await updateOrderItem(
        props.order.id,
        item.itemOrderID,
        token,
      );
      const allItemsReady = (props.order.items ?? []).every((i) => i.isReady);
      if (res === true || allItemsReady) {
        dragToReady(props.order.id, token);
      }
    }
  };

  const handlePrint = async () => {
    if (printLoading || orderCardRef.current === null) return;
    setPrintLoading(true);
    try {
      await printOrderTicket(orderCardRef.current);
    } catch (error) {
      const code = error instanceof Error ? error.message : "PRINT_FAILED";
      const keyByCode: Record<string, string> = {
        NO_PRINTER_SELECTED: "No printer selected",
        INVALID_RECEIPT_IMAGE: "Invalid receipt image",
        PRINT_TIMED_OUT: "Print timed out",
        PRINT_FAILED: "Print failed",
      };
      window.alert(
        t(
          keyByCode[code] ??
            (code.includes("No printer selected")
              ? "No printer selected"
              : "Print failed"),
        ),
      );
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <div
      //ref={(card) => (orderCardRef.current = card)}
      className={`order-container ${
        orderFade ? "fade" : moveLeft ? "moveLeft" : ""
      }`}
      onAnimationStart={() => moveLeft && setOpenBody(false)}
      onAnimationEnd={() => {
        setOrderFade(false);
        setMoveLeft(false);
      }}
    >
      <header
        className="order-header"
        style={{
          backgroundColor:
            props.order.isPreparing || props.order.isReady
              ? Colors.GREEN
              : Colors.PURPLE,
        }}
        onClick={() => setOpenBody(!openBody)}
      >
        <div className="order-ticket-print-source">
          <OrderTicketV1 ref={orderCardRef} order={props.order} />
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          {printLoading ? (
            <CircularProgress
              className="MuiCircularProgress-colorPrimary"
              size={28}
            />
          ) : (
            <AiOutlinePrinter
              onClick={handlePrint}
              color={Colors.WHITE}
              size={40}
              className="btn"
            />
          )}
        </div>
        <div
          style={{
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <p
            className="order-header-text"
            style={{
              color:
                props.order.isPreparing || props.order.isReady
                  ? Colors.PURPLE
                  : Colors.GREEN,
            }}
          >
            {t("Order number")}
          </p>
          <p
            className="order-header-text"
            style={{
              color:
                props.order.isPreparing || props.order.isReady
                  ? Colors.PURPLE
                  : Colors.GREEN,
            }}
          >
            {props.order.id % 1000}
          </p>
        </div>
        <div
          style={{
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <p
            className="order-header-text"
            style={{
              color:
                props.order.isPreparing || props.order.isReady
                  ? Colors.PURPLE
                  : Colors.GREEN,
            }}
          >
            {t("Ordered at")}
          </p>
          <p
            className="order-header-text"
            style={{
              color:
                props.order.isPreparing || props.order.isReady
                  ? Colors.PURPLE
                  : Colors.GREEN,
            }}
          >
            {convertDate()}
          </p>
        </div>
      </header>
      {openBody && (
        <div className="order-items-container">
          {(props.order.items ?? []).map((item) => (
            <OrderCardItem
              key={item.itemOrderID}
              item={item}
              done={props.order.isReady}
              cancelled={props.order.refused}
              isPreparing={props.order.isPreparing}
              isWaiting={props.order.isWaiting}
              orderID={props.order.id}
              updateItem={updateItem}
            />
          ))}
        </div>
      )}
      {openBody && (
        <div className="order-details">
          {openBody && props.order.isPreparing && (
            <div
              style={{
                fontFamily:
                  i18n.language === "ar"
                    ? "'Cairo', sans-serif"
                    : "'Rubik', sans-serif",
              }}
              className="order-timer"
            >
              {mins < 10 && 0}
              {mins}:{secs < 10 && 0}
              {secs}
            </div>
          )}
          <div className="order-details-row">
            <p>{t("Payment method")}</p>
            <div className="order-payment-method">
              {props.order.payMethod === "card" ? (
                <FaCreditCard color={Colors.PURPLE} size={24} />
              ) : (
                <FaMoneyBill color={Colors.PURPLE} size={24} />
              )}
              <p>{t(props.order.payMethod === "card" ? "card" : "cash")}</p>
            </div>
          </div>
          <div className="order-details-row">
            <p>{t("Orderer")}</p>
            <p>{t(props.order.orderer)}</p>
          </div>
          <div className="order-details-row">
            <p>{t("Price")}</p>
            <p>{props.order.price}₪</p>
          </div>
          <div className="order-details-row">
            <p>{t("Notes")}</p>
            <label className="order-details-row-notes-textarea">
              {t(props.order.orderNotes)}
            </label>
          </div>
          {props.order.isDelivery && (
            <div className="order-details-row">
              <p>{t("Courier")}</p>
              <p>
                {t(
                  props.order.deliverBy !== null
                    ? props.order.deliverBy.first_name +
                        " " +
                        props.order.deliverBy.last_name
                    : "",
                )}
              </p>
            </div>
          )}
          {props.order.isAtRestaurant && <h2>{t("Eating at place")}</h2>}
        </div>
      )}

      {openBody && props.order.isWaiting && (
        <button
          disabled={startButtonLoading}
          onClick={() => {
            setStartButtonLoading(true);
            setPopUpOpen(true);
          }}
          style={{
            fontFamily:
              i18n.language === "ar"
                ? "'Cairo', sans-serif"
                : "'Rubik', sans-serif",
          }}
          className="order-start-button"
        >
          {startButtonLoading ? (
            <CircularProgress
              className="MuiCircularProgress-colorPrimary"
              size={20}
            />
          ) : (
            t("Start order")
          )}
        </button>
      )}
      {openBody &&
        !props.order.isReady &&
        !props.order.refused &&
        !props.order.isPreparing && (
          <button
            disabled={cancelButtonLoading}
            onClick={async () => {
              setOrderFade(true);
              setCancelButtonLoading(true);
              token !== null &&
                (await updateOrder(
                  props.order.id,
                  OrderStatus.CANCELLED,
                  token,
                ).catch((er) => console.error(er)));
            }}
            style={{
              fontFamily:
                i18n.language === "ar"
                  ? "'Cairo', sans-serif"
                  : "'Rubik', sans-serif",
            }}
            className="order-start-button"
          >
            {cancelButtonLoading ? (
              <CircularProgress
                className="MuiCircularProgress-colorPrimary"
                size={20}
              />
            ) : (
              t("Cancel order")
            )}
          </button>
        )}
      {openBody &&
        props.order.isReady &&
        props.order.isPickUp &&
        props.order.payMethod === "cash" &&
        (noShowReported ? (
          <p className="order-details-row">
            {t("Reported to the supervisor for review")}
          </p>
        ) : (
          <>
            <button
              disabled={cashActionLoading !== null}
              onClick={() => setCashConfirmReason("customer_no_show")}
              style={{
                fontFamily:
                  i18n.language === "ar"
                    ? "'Cairo', sans-serif"
                    : "'Rubik', sans-serif",
              }}
              className="order-start-button"
            >
              {cashActionLoading === "no_show" ? (
                <CircularProgress
                  className="MuiCircularProgress-colorPrimary"
                  size={20}
                />
              ) : (
                t("Customer did not come to pickup")
              )}
            </button>
            <button
              disabled={cashActionLoading !== null}
              onClick={() => setCashConfirmReason("refuse_to_pay")}
              style={{
                fontFamily:
                  i18n.language === "ar"
                    ? "'Cairo', sans-serif"
                    : "'Rubik', sans-serif",
              }}
              className="order-start-button"
            >
              {cashActionLoading === "refuse" ? (
                <CircularProgress
                  className="MuiCircularProgress-colorPrimary"
                  size={20}
                />
              ) : (
                t("Customer does not want to pay")
              )}
            </button>
            <button
              disabled={cashActionLoading !== null}
              onClick={async () => {
                if (token === null) return;
                setCashActionLoading("picked_up");
                const res = await updateOrderPickUp(props.order.id, token);
                setCashActionLoading(null);
                if (res && res.status !== 200) {
                  window.alert(res.message || t("Something went wrong"));
                }
              }}
              style={{
                fontFamily:
                  i18n.language === "ar"
                    ? "'Cairo', sans-serif"
                    : "'Rubik', sans-serif",
              }}
              className="order-start-button"
            >
              {cashActionLoading === "picked_up" ? (
                <CircularProgress
                  className="MuiCircularProgress-colorPrimary"
                  size={20}
                />
              ) : (
                t("Picked up by user")
              )}
            </button>
          </>
        ))}
      <AppPopup
        xButton
        open={cashConfirmReason !== null}
        setOpen={(open: boolean) => {
          if (!open) setCashConfirmReason(null);
        }}
        title={t("Report to supervisor?")}
        description={
          cashConfirmReason === "refuse_to_pay"
            ? t(
                "This flags the order for a supervisor because the customer refused to pay. The supervisor can charge the backup card.",
              )
            : t(
                "This flags the order for a supervisor because the customer did not come to pick up. The supervisor can charge the backup card.",
              )
        }
        choices={[
          <button
            key="confirm"
            style={{
              fontFamily:
                i18n.language === "ar"
                  ? "'Cairo', sans-serif"
                  : "'Rubik', sans-serif",
            }}
            className="popup_modal_choice"
            onClick={async (event) => {
              event.preventDefault();
              const reason = cashConfirmReason;
              setCashConfirmReason(null);
              if (token === null || reason === null) return;
              setCashActionLoading(
                reason === "refuse_to_pay" ? "refuse" : "no_show",
              );
              const res = await reportNoShow(props.order.id, token, reason);
              setCashActionLoading(null);
              if (res.status === 200) {
                setNoShowReported(true);
              } else {
                window.alert(res.message);
              }
            }}
          >
            {t("Yes, report it")}
          </button>,
          <button
            key="cancel"
            style={{
              fontFamily:
                i18n.language === "ar"
                  ? "'Cairo', sans-serif"
                  : "'Rubik', sans-serif",
            }}
            className="popup_modal_choice"
            onClick={(event) => {
              event.preventDefault();
              setCashConfirmReason(null);
            }}
          >
            {t("Cancel")}
          </button>,
        ]}
      />
      <AppPopup
        xButton
        open={popupOpen}
        setOpen={setPopUpOpen}
        title={t("Time to prepare")}
        description={t(
          "Please choose how much time it takes to prepare this order",
        )}
        choices={[5, 10, 15, 20, 25, 30, 35, 40].map((minutes) => (
          <button
            key={minutes}
            style={{
              fontFamily:
                i18n.language === "ar"
                  ? "'Cairo', sans-serif"
                  : "'Rubik', sans-serif",
            }}
            className="popup_modal_choice"
            onClick={(event) => {
              event.preventDefault();
              setPopUpOpen(false);
              if (token !== null) {
                const timeStr = `00:${String(minutes).padStart(2, "0")}:00`;
                // One request: status + duration. A separate setEstimatedTime
                // raced orderUpdate, whose full save wrote the 20-minute
                // model default back over the kitchen's choice.
                updateOrder(
                  props.order.id,
                  OrderStatus.PREPARING,
                  token,
                  timeStr,
                );
              }
            }}
          >
            {minutes}
          </button>
        ))}
      />
    </div>
  );
};

// const OrderTicketStyles: ReactPDF.Styles = {
//   container: {
//     justifyContent: "flex-start",
//     flexDirection: "column",
//     padding: 50,
//   },
//   header: {
//     justifyContent: "space-between",
//     flexDirection: "row",
//     alignItems: "center",
//   },
//   title: {
//     alignSelf: "center",
//     marginTop: 20,
//   },
//   itemsContainer: {
//     justifyContent: "space-between",
//     flexDirection: "column",
//     marginTop: 20,
//     borderTop: 2,
//     borderBottom: 2,
//     paddingVertical: 5,
//   },
//   item: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: "3%",
//   },
//   itemRight: {
//     flexDirection: "row",
//     //alignItems: 'flex-start',
//   },
//   itemTitleWithSubItems: {
//     //flex: 0.8,
//     flexDirection: "column",
//     marginLeft: "3%",
//     alignItems: "flex-start",
//   },
//   itemTitle: {
//     flexDirection: "row",
//   },
//   itemSubItems: {
//     flexDirection: "column",
//     alignItems: "flex-start",
//   },
//   subItem: {
//     flexDirection: "row",
//   },
//   total: {
//     justifyContent: "space-between",
//     flexDirection: "row",
//     alignItems: "center",
//     marginTop: 20,
//   },
// };

export default observer(OrderCard);
